"use client";

import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

import { trackSubscription } from "@/lib/analytics";
import { clearSessionCache } from "@/lib/storage";

interface StripeRedirectHandlerProps {
  onNotification: (notification: { type: "success" | "error"; message: string }) => void;
}

export function StripeRedirectHandler({ onNotification }: StripeRedirectHandlerProps) {
  const t = useTranslations("premium");
  const searchParams = useSearchParams();
  const { update } = useSession();

  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    const portal = searchParams.get("portal");

    const refreshPremiumStatus = async () => {
      try {
        // Clear the premium cache on the server to force a fresh database fetch
        await fetch("/api/session/refresh-premium", { method: "POST" });

        // Clear the session cache in localStorage to force fresh data
        clearSessionCache();

        // Now refresh the session to get the updated premium status
        await update();
      } catch (error) {
        console.error("Failed to refresh premium status:", error);
        // Clear cache and try to update session even if API call fails
        clearSessionCache();
        await update();
      }
    };

    if (success === "true") {
      // Refresh session to get updated premium status
      refreshPremiumStatus();

      // Track successful subscription completion
      trackSubscription("completed");

      onNotification({
        type: "success",
        message: t("welcomeToPremium"),
      });
      // Clear the URL parameter
      window.history.replaceState({}, "", "/");
    } else if (canceled === "true") {
      onNotification({
        type: "error",
        message: t("paymentCanceled"),
      });
      // Clear the URL parameter
      window.history.replaceState({}, "", "/");
    } else if (portal === "success") {
      // Refresh session after returning from customer portal
      refreshPremiumStatus();

      onNotification({
        type: "success",
        message: t("subscriptionUpdated"),
      });
      // Clear the URL parameter
      window.history.replaceState({}, "", "/");
    }
  }, [searchParams, onNotification, update, t]);

  return null; // This component doesn't render anything
}
